import { useEffect, useState } from 'react';
import { HiArrowRight } from 'react-icons/hi';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tag,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { deleteTieredPricing, getTieredPricing, upsertTieredPricing } from 'api/internaltools';
import { showError, showToast } from 'utils/notifications';

const EMPTY_TIER = { min_volume: '', max_volume: '', ABS: '', BPS: '' };

const TIERED_PRICING_TYPES = [
  { value: 'transaction', label: 'Transaction' },
  { value: 'entity', label: 'Entity' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'corridor', label: 'Corridor' },
];

const PERIODICITY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export default function TieredPricingDialog({
  entityId,
  corridorId,
  sourceCurrency,
  destinationCurrency,
  isOpen,
  onClose,
  entities = [],
  selectedEntityId,
  onEntityChange,
}) {
  const toast = useToast();
  const [tiers, setTiers] = useState([{ ...EMPTY_TIER }]);
  const [type, setType] = useState('entity');
  const [periodicity, setPeriodicity] = useState('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);

  const activeEntityId = selectedEntityId || entityId;

  const fetchExistingConfig = async () => {
    if (!activeEntityId || !corridorId) return;
    try {
      setIsFetching(true);
      const response = await getTieredPricing(activeEntityId, corridorId);
      const config = response?.data?.data;

      if (config && config.tiers && config.tiers.length > 0) {
        setHasExisting(true);
        setType(config.type || 'entity');
        setPeriodicity(config.periodicity || 'monthly');
        setTiers(
          config.tiers.map((t) => ({
            min_volume: String(t.min_volume ?? ''),
            max_volume: String(t.max_volume ?? ''),
            ABS: String(t.ABS ?? ''),
            BPS: String(t.BPS ?? ''),
          }))
        );
      } else {
        setHasExisting(false);
        resetForm();
      }
    } catch (error) {
      showError(toast, 'Error fetching tiered pricing config.', error);
    } finally {
      setIsFetching(false);
    }
  };

  const resetForm = () => {
    setTiers([{ ...EMPTY_TIER }]);
    setType('entity');
    setPeriodicity('monthly');
  };

  useEffect(() => {
    if (isOpen) {
      fetchExistingConfig();
    } else {
      resetForm();
      setHasExisting(false);
    }
  }, [isOpen, activeEntityId, corridorId]);

  const handleTierChange = (index, field, value) => {
    const updated = [...tiers];

    updated[index] = { ...updated[index], [field]: value };
    setTiers(updated);
  };

  const addTier = () => {
    setTiers([...tiers, { ...EMPTY_TIER }]);
  };

  const removeTier = (index) => {
    if (tiers.length <= 1) return;
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const validateTiers = () => {
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];

      if (tier.min_volume === '' || tier.max_volume === '' || tier.ABS === '' || tier.BPS === '') {
        return 'All tier fields must be filled.';
      }
      if (Number(tier.min_volume) < 0) {
        return `Tier ${i + 1}: min_volume cannot be negative.`;
      }
      if (Number(tier.max_volume) !== -1 && Number(tier.max_volume) <= Number(tier.min_volume)) {
        return `Tier ${i + 1}: max_volume must be greater than min_volume (or -1 for unlimited).`;
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateTiers();

    if (validationError) {
      showToast(toast, 'Validation Error', validationError, 'error');

      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        entity_id: activeEntityId,
        corridor_id: corridorId,
        type,
        periodicity,
        tiers: tiers.map((t) => ({
          min_volume: Number(t.min_volume),
          max_volume: Number(t.max_volume),
          ABS: Number(t.ABS),
          BPS: Number(t.BPS),
        })),
      };

      await upsertTieredPricing(payload);
      showToast(toast, 'Success', `Tiered pricing ${hasExisting ? 'updated' : 'created'} successfully.`, 'success');
      onClose();
    } catch (error) {
      showError(toast, 'Error saving tiered pricing config.', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove tiered pricing for this entity and corridor?')) return;

    setIsDeleting(true);
    try {
      await deleteTieredPricing(activeEntityId, corridorId);
      showToast(toast, 'Success', 'Tiered pricing removed successfully.', 'success');
      onClose();
    } catch (error) {
      showError(toast, 'Error removing tiered pricing config.', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} closeOnEsc={false} size='xl'>
      <ModalOverlay />
      <ModalContent maxW='700px'>
        <ModalHeader>{hasExisting ? 'Edit' : 'Add'} Tiered Pricing</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={2} align='center' justify='center' mb={4}>
            <Tag size='sm' colorScheme='green'>
              {sourceCurrency}
            </Tag>
            <HiArrowRight />
            <Tag size='sm' colorScheme='blue'>
              {destinationCurrency}
            </Tag>
          </Flex>

          {entities.length > 0 && (
            <FormControl mb={4}>
              <FormLabel>Entity</FormLabel>
              <Select value={selectedEntityId} onChange={(e) => onEntityChange(e.target.value)}>
                {entities.map((entity) => (
                  <option key={entity.Id || entity.id} value={entity.Id || entity.id}>
                    {entity.Name || entity.name || entity.Id || entity.id}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}

          {isFetching ? (
            <Text textAlign='center' color='gray.500'>
              Loading...
            </Text>
          ) : (
            <VStack spacing={4} align='stretch'>
              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Type</FormLabel>
                  <Select value={type} onChange={(e) => setType(e.target.value)}>
                    {TIERED_PRICING_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Periodicity</FormLabel>
                  <Select value={periodicity} onChange={(e) => setPeriodicity(e.target.value)}>
                    {PERIODICITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <Text fontWeight='bold' mt={2}>
                Tiers
              </Text>

              {tiers.map((tier, index) => (
                <Box key={index} p={3} borderWidth='1px' borderRadius='md' bg='gray.50'>
                  <Flex justify='space-between' align='center' mb={2}>
                    <Text fontSize='sm' fontWeight='semibold'>
                      Tier {index + 1}
                    </Text>
                    {tiers.length > 1 && (
                      <IconButton
                        icon={<DeleteIcon />}
                        size='xs'
                        colorScheme='red'
                        variant='ghost'
                        onClick={() => removeTier(index)}
                        aria-label='Remove tier'
                      />
                    )}
                  </Flex>
                  <HStack spacing={2}>
                    <FormControl>
                      <FormLabel fontSize='xs'>Min Volume (in USD)</FormLabel>
                      <Input
                        type='number'
                        size='sm'
                        placeholder='0'
                        value={tier.min_volume}
                        onChange={(e) => handleTierChange(index, 'min_volume', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize='xs'>Max Volume (in USD)</FormLabel>
                      <Input
                        type='number'
                        size='sm'
                        placeholder='-1 for unlimited'
                        value={tier.max_volume}
                        onChange={(e) => handleTierChange(index, 'max_volume', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize='xs'>ABS (in Source Currency)</FormLabel>
                      <Input
                        type='number'
                        size='sm'
                        placeholder='0'
                        value={tier.ABS}
                        onChange={(e) => handleTierChange(index, 'ABS', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize='xs'>BPS (in Source Currency)</FormLabel>
                      <Input
                        type='number'
                        size='sm'
                        placeholder='0'
                        value={tier.BPS}
                        onChange={(e) => handleTierChange(index, 'BPS', e.target.value)}
                      />
                    </FormControl>
                  </HStack>
                </Box>
              ))}

              <Button leftIcon={<AddIcon />} size='sm' variant='outline' onClick={addTier}>
                Add Tier
              </Button>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {hasExisting && (
            <Button
              size='sm'
              colorScheme='red'
              variant='outline'
              onClick={handleDelete}
              isLoading={isDeleting}
              isDisabled={isFetching || isLoading}
              mr='auto'
            >
              Remove Tiered Pricing
            </Button>
          )}
          <Button variant='ghost' size='sm' onClick={onClose} mr={2}>
            Cancel
          </Button>
          <Button
            size='sm'
            colorScheme='blue'
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={isFetching || isDeleting}
          >
            {hasExisting ? 'Update' : 'Create'} Tiered Pricing
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
